import { FC, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { CustomTable } from "../../../components/CustomTable/CustomTable";
import { CustomButtonTable } from "../../../components/CustomButtonTable/CustomButtonTable";
import { CustomTypography } from "../../../components/CustomTypography/CustomTypography";
import { colors } from "../../../styles/colors";
import {
  TCompanyInvitation,
  useGetCompanyInvitations,
  useResendCompanyInvitation,
  useRevokeCompanyInvitation,
} from "../../../api/invitations";

/**
 * Les invitations en cours d'une societe, sous la liste des utilisateurs.
 *
 * ── CE QUE CE PANNEAU REPARE ────────────────────────────────────────────────
 *
 * L'ecran Utilisateurs ne lit que `company_user` : une personne invitee qui n'a
 * pas fini son accueil n'y apparait pas. On invitait un gestionnaire de flotte
 * et l'ecran affichait, imperturbable, "Nombre d'utilisateurs : 0" et "Aucune
 * donnee" — rien ne distinguait "jamais invite" de "invite, en attente" de
 * "invite, lien expire depuis cinq jours".
 *
 * Ce panneau rend l'invitation visible la ou on la cherche, avec les deux
 * actions qui permettent de la reparer sans ouvrir un client SQL : relancer
 * (renouvelle la fenetre de 72 h et renvoie le mail) et revoquer.
 *
 * Les invitations DEJA acceptees ne sont pas listees : la personne figure alors
 * dans le tableau du dessus, et la repeter ici ferait douter de laquelle des
 * deux lignes fait foi.
 */

type Props = {
  companyId: number;
};

const STATUS_COLORS: Record<string, string> = {
  pending: colors.primary,
  expired: colors.error,
  revoked: colors.greydark,
};

export const PendingInvitations: FC<Props> = ({ companyId }) => {
  const { t } = useTranslation();

  const { invitations, isPending } = useGetCompanyInvitations(companyId);
  const { mutate: resend, isPending: isResending } =
    useResendCompanyInvitation(companyId);
  const { mutate: revoke, isPending: isRevoking } =
    useRevokeCompanyInvitation(companyId);

  /*
   * L'API renvoie deja `expired` pour une invitation `pending` hors delai, donc
   * aucun calcul de date ici : une seule source pour l'etat, et l'ecran ne peut
   * pas diverger du serveur.
   */
  const openInvitations = useMemo(
    () => invitations.filter((i) => i.status !== "accepted"),
    [invitations]
  );

  const formatDate = (value: string | null) =>
    value
      ? new Date(value).toLocaleDateString("fr-FR", {
          day: "numeric",
          month: "short",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
        })
      : "—";

  const columns = [
    { header: t("common.email", "Email"), field: "company_email" },
    { header: t("user.name", "Name"), field: "lastname" },
    { header: t("user.firstname", "Firstname"), field: "firstname" },
    { header: t("user.role", "Rôle"), field: "company_role" },
    {
      header: t("common.status", "Statut"),
      field: "status",
      render: (rowData: Record<string, any>) => {
        const row = rowData as TCompanyInvitation;
        return (
        <CustomTypography
          variant={"link"}
          fontWeight={"bold"}
          textDecoration={"none"}
          color={STATUS_COLORS[row.status] ?? colors.darkblack}
          text={
            row.status === "pending"
              ? t("invitation.statusPending", "En attente")
              : row.status === "expired"
              ? t("invitation.statusExpired", "Expirée")
              : t("invitation.statusRevoked", "Révoquée")
          }
        />
        );
      },
    },
    {
      header: t("invitation.expiresAt", "Expire le"),
      field: "expires_at",
      render: (rowData: Record<string, any>) => (
        <CustomTypography
          variant={"link"}
          textDecoration={"none"}
          color={colors.darkblack}
          text={formatDate((rowData as TCompanyInvitation).expires_at)}
        />
      ),
    },
    {
      header: "",
      field: "",
      render: (rowData: Record<string, any>) => {
        const row = rowData as TCompanyInvitation;
        return (
        <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
          {/*
            Relancer accepte une invitation EXPIREE (le serveur ne refuse que
            `accepted` et `revoked`) : c'est donc la reparation du cas le plus
            frequent, pas seulement un rappel.
          */}
          <CustomButtonTable
            variant={"secondary"}
            textVariant={"link"}
            label={t("invitation.resend", "Relancer")}
            width={"fit-content"}
            padding={"0px 12px"}
            textDecoration={"none"}
            onClick={() => resend(row.invitation_code)}
          />
          {row.status === "pending" && (
            <CustomButtonTable
              variant={"secondary"}
              textVariant={"link"}
              label={t("invitation.revoke", "Révoquer")}
              width={"fit-content"}
              padding={"0px 12px"}
              textDecoration={"none"}
              onClick={() => revoke(row.invitation_code)}
            />
          )}
        </div>
        );
      },
    },
  ];

  // Rien a montrer : on n'ajoute pas un tableau vide sous un tableau vide.
  if (isPending || openInvitations.length === 0) {
    return null;
  }

  return (
    <div style={{ marginTop: "24px", opacity: isResending || isRevoking ? 0.6 : 1 }}>
      <CustomTable
        columns={columns}
        data={openInvitations}
        showPagination={false}
        tableMaxHeight={"320px"}
        titleHeader={t("invitation.listTitle", "Invitations en cours")}
      />
    </div>
  );
};
